import { UserInfo } from '@mygpt/app/apis';
import { Http } from '@mygpt/app/http';
import { ActiveAddOnSubscription, ActivePlanSubscription } from '@mygpt/app/users';

/**
 * 获取用户信息
 * @param params
 * @returns
 */
export const getUserInfo = () => {
  const { get } = Http.use();
  return get<UserInfo>(`/users/me`);
};

export interface SendEmailVerificationParams {
  email: string;
}

/**
 * 发送邮箱验证
 * @param params
 * @returns
 */
export const sendEmailVerification = (params: SendEmailVerificationParams) => {
  const { ...rest } = params;
  const { post } = Http.use();
  return post(`/users/email-verifications`, rest);
};

export interface PatchUserConfigParams {
  [key: string]: boolean;
}
/**
 * 修改用户设置
 * @param params
 * @returns
 */
export const patchUserConfig = (params: PatchUserConfigParams) => {
  const { patch } = Http.use();
  return patch(`/users/configs`, { configs: params });
};

export interface GetUserQuotaParams {
  user_id: string;
}

export interface EffectiveQuotas {
  is_corporate: boolean;
  bot_quota: {
    used: number;
    limit: number;
    available: number;
  };
  message_quota: {
    used: number;
    limit: number;
    available: number;
  };
  web_page_quota: {
    used: number;
    limit: number;
    available: number;
  };
  multi_modal_parsing_quota: {
    used: number;
    limit: number;
    available: number;
  };
}

/**
 * 超级管理员对用户的配置覆写。后端 key 白名单见
 * felo-mygpt: mygpt/endpoints/user_quota.py:ALLOWED_OVERRIDE_KEYS
 * 未覆写时该字段为 null；新增 key 时同步扩展此接口。
 */
export interface AdminOverrides {
  /** 单文件上传上限（MB），覆盖系统默认 100MB */
  max_file_size_mb?: number;
}

export interface UserQuota {
  user: {
    id: string;
    user_id: string;
    email: string;
    name: string;
    picture: string;
    locale: string;
    corporate: boolean;
    is_independent: boolean;
  };
  active_add_on_subscriptions: ActiveAddOnSubscription[];
  active_plan_subscription: ActivePlanSubscription;
  effective_quotas: EffectiveQuotas;
  /** 超级管理员对该用户的配置覆写；null 表示无覆写，走套餐 baseline / 系统默认 */
  admin_overrides?: AdminOverrides | null;
}

/**
 * 获取用户套餐余量
 * @param params
 * @returns
 */
export const getUserQuota = (params: GetUserQuotaParams) => {
  const { get } = Http.use();
  return get(`/user/quota/${params.user_id}`);
};

/**
 * 获取当前用户的配额信息
 * @returns 当前用户的配额信息
 */
export const getQuotaInfo = () => {
  const { get } = Http.use();
  return get(`/user/quota`);
};

/**
 * 超级管理员更新目标用户的 admin overrides。
 * 传 admin_overrides=null 等同于清空所有覆写，恢复套餐 baseline / 系统默认。
 *
 * @param userId 目标用户 id（注意是 user 表的主键 id，不是 user_id 字段）
 * @param admin_overrides 完整 overrides 对象或 null
 */
export const updateUserAdminOverrides = (
  userId: string,
  admin_overrides: AdminOverrides | null,
) => {
  const { patch } = Http.use();
  return patch(`/user/quota/${userId}/overrides`, { admin_overrides });
};

export interface GetBotQuotaParams {
  botId: string;
}

export interface BotQuota {
  effective_quotas: EffectiveQuotas;
}

/**
 * 获取机器人套餐余量
 * @param params
 * @returns
 */
export const getBotQuota = (params: GetBotQuotaParams) => {
  const { get } = Http.use();
  return get<BotQuota>(`/user/quota/bot/${params.botId}`);
};

export interface RealtimeConsentResponse {
  consented: boolean;
  consented_at?: string | null;
  version?: string | null;
}

/**
 * 获取当前用户对 Realtime 同意声明的记录
 * 后端需实现：GET /users/me/realtime-consent
 * 返回 { consented, consented_at, version }
 */
export const getRealtimeConsent = () => {
  const { get } = Http.use();
  return get<RealtimeConsentResponse>(`/users/me/realtime-consent`);
};

export interface SaveRealtimeConsentParams {
  version: string;
}

/**
 * 保存当前用户对 Realtime 同意声明的记录
 * 后端需实现：POST /users/me/realtime-consent body { version }
 */
export const saveRealtimeConsent = (params: SaveRealtimeConsentParams) => {
  const { post } = Http.use();
  return post(`/users/me/realtime-consent`, params);
};
